import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useRentals } from '../../entities/rental';
import { Table, Button, Form, InputGroup } from 'react-bootstrap';
import { IconPlus, IconSearch, IconSelector } from '@tabler/icons-react';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { useDateFormat } from '../../shared/DateFormatContext';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import { useSortPreference } from '../../shared/useSortPreference';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import EmptyState from '../../components/feedback/EmptyState';
import StatusBadge from '../../components/ui/StatusBadge';
import AppButton from '../../components/ui/AppButton';
import AppTable from '../../components/ui/AppTable';

const RENTAL_STATUS_COLORS = {
  active: '#2fb344',
  inactive: '#f76707',
  completed: '#206bc4',
  expired: '#d63939',
  canceled: '#6c7a91',
  cancelled: '#6c7a91',
  pending_return: '#4263eb',
  ongoing: '#12b886',
};

const Rentals = () => {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const { canManage } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useSortPreference('rentals-field', 'date_from');
  const [sortDirection, setSortDirection] = useSortPreference('rentals-dir', 'asc');

  const { data: rentals = [], isLoading } = useRentals();

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const sortedRentals = [...rentals].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'name':
        comparison = String(a.id).localeCompare(String(b.id));
        break;
      case 'date_from':
        comparison = new Date(a.date_from) - new Date(b.date_from);
        break;
      case 'date_to':
        comparison = new Date(a.date_to) - new Date(b.date_to);
        break;
      case 'customer':
        comparison = a.customer_name.localeCompare(b.customer_name);
        break;
      case 'status':
        comparison = a.rental_status.localeCompare(b.rental_status);
        break;
      case 'reason':
        comparison = (a.rental_reason_name || '').localeCompare(b.rental_reason_name || '');
        break;
      case 'issued_by':
        comparison = a.issued_by_name.localeCompare(b.issued_by_name);
        break;
      default:
        comparison = 0;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const filteredRentals = sortedRentals.filter(rental => {
    const lowerNeedle = searchTerm.toLowerCase();

    return (
      rental.id.toString().includes(searchTerm) ||
      (rental.customer_name || '').toLowerCase().includes(lowerNeedle) ||
      (rental.issued_by_name || '').toLowerCase().includes(lowerNeedle) ||
      (rental.rental_reason_name || '').toLowerCase().includes(lowerNeedle) ||
      (rental.rental_status_display || '').toLowerCase().includes(lowerNeedle)
    );
  });

  const columns = useMemo(() => [
    {
      key: 'name',
      label: t('rentals.tableHeaders.name'),
      sortable: true,
      render: (row) => (
        <Link to={`/rentals/rental/${row.id}`}>{t('rentals.rentalName', { id: row.id })}</Link>
      ),
    },
    {
      key: 'date_from',
      label: t('rentals.tableHeaders.from'),
      sortable: true,
      render: (row) => formatDate(row.date_from),
    },
    {
      key: 'date_to',
      label: t('rentals.tableHeaders.to'),
      sortable: true,
      render: (row) => formatDate(row.date_to),
    },
    {
      key: 'customer',
      label: t('rentals.tableHeaders.customer'),
      sortable: true,
      render: (row) => row.customer ? (
        <Link to={`/customers/customer/${row.customer}`}>
          {row.customer_details?.resp_person || row.customer_name || t('common.notSet')}
        </Link>
      ) : t('common.notSet'),
    },
    {
      key: 'status',
      label: t('rentals.tableHeaders.status'),
      sortable: true,
      render: (row) => (
        <StatusBadge
          color={RENTAL_STATUS_COLORS[row.rental_status] || '#6c7a91'}
          label={row.rental_status_display || t(`rentals.statuses.${row.rental_status}`) || t('common.notSet')}
        />
      ),
    },
    {
      key: 'reason',
      label: t('rentals.tableHeaders.reason'),
      sortable: true,
      render: (row) => row.rental_reason_details?.name || row.rental_reason_name || t('common.notSet'),
    },
    {
      key: 'issued_by',
      label: t('rentals.tableHeaders.issuedBy'),
      sortable: true,
      render: (row) => row.issued_by_details?.display_name || row.issued_by_name || t('common.notSet'),
    },
  ], [t]);

  const hasActiveFilters = searchTerm.trim().length > 0;

  return (
    <PageShell
      pretitle={t('rentals.overview')}
      title={t('rentals.title')}
      description={t('rentals.subtitle')}
      actions={canManage ? (
          <AppButton as={Link} to="/rentals/add" variant="primary" icon={<IconPlus size={16} />}>
            {t('rentals.newRental')}
          </AppButton>
        ) : null}
    >
      <PageSection
        title={t('rentals.title')}
        description={t('rentals.listDescription')}
        actions={
          <InputGroup className="app-search">
            <InputGroup.Text>
              <IconSearch size={16} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('rentals.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        }
        bodyClassName="p-0"
      >
        {isLoading ? (
          <CenteredLoader />
        ) : (
          <AppTable
            columns={columns}
            data={filteredRentals}
            rowKey="id"
            loading={isLoading}
            sortKey={sortField}
            sortOrder={sortDirection}
            onSort={(field) => handleSort(field)}
            emptyTitle={hasActiveFilters ? t('rentals.noResultsTitle') : t('rentals.noRentals')}
            emptyMessage={hasActiveFilters ? t('rentals.noResultsMessage') : t('rentals.emptyMessage')}
            emptyAction={canManage && !hasActiveFilters ? (
              <AppButton as={Link} to="/rentals/add" variant="primary" icon={<IconPlus size={16} />}>
                {t('rentals.newRental')}
              </AppButton>
            ) : undefined}
          />
        )}
      </PageSection>
    </PageShell>
  );
};

export default Rentals;
